import DateFnsUtils from "@date-io/date-fns";
import {
  Box,
  Button,
  CardMedia,
  FormControlLabel,
  FormHelperText,
  Grid,
  makeStyles,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import CalendarTodayOutlinedIcon from "@material-ui/icons/CalendarTodayOutlined";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import moment from "moment";
import React, { useState } from "react";
import {
  Controller,
  UseFormGetValues,
  UseFormHandleSubmit,
} from "react-hook-form";
import { countries } from "../constant/countries";
import tick from "../icons/tick.svg";
import ModalContainer from "./ModalContainer";

interface StaffFormFieldsProps {
  register: any;
  getValues: UseFormGetValues<any>;
  control: any;
  errors: any;
  signature?: string;
  signatureDate?: Date;
  setOpenModal?: (open: boolean) => void;
  onConfirmSignature?: () => void;
  handleSubmit?: UseFormHandleSubmit<any>;
  onSubmit?: (data: any) => void;
  onError?: (errors: any) => void;
  isReadOnly?: boolean;
  hideButtons?: boolean;
  dirtyFields?: Partial<Record<string, boolean>>;
  trigger?: () => Promise<boolean>;
}

const StaffFormFields: React.FC<StaffFormFieldsProps> = ({
  register,
  getValues,
  control,
  errors,
  signature,
  signatureDate,
  setOpenModal,
  onConfirmSignature,
  handleSubmit,
  onSubmit,
  onError,
  isReadOnly,
  hideButtons,
  dirtyFields,
}) => {
  const classes = useStyles({ isReadOnly });
  const [openPasswordModal, setOpenPasswordModal] = useState(false);



  // Helper function to check if a field is modified
  const isFieldModified = (fieldName: string) => {
    return dirtyFields?.[fieldName] === true;
  };

  const handlePasswordVerified = () => {
    setOpenPasswordModal(false);
    if (handleSubmit) {
      handleSubmit(onSubmit, onError)();
    }
  };

  const handleComplete = () => {
    setOpenPasswordModal(true);
  };

  return (
    <>
      {/* Personal Information Section */}
      <Box className={`${classes.formSection} formSection`}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} data-field="english_name-grid">
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>英文姓名*</Typography>
              <Box
                className={`${classes.inputField} ${
                  isFieldModified("english_name") ? "Mui-modified" : ""
                }`}
              >
                <TextField
                  {...register("english_name", {
                    required: "此欄位必填",
                    maxLength: { value: 60, message: "英文姓名最多60個字符" },
                  })}
                  fullWidth
                  variant="outlined"
                  error={!!errors.english_name}
                  helperText={errors.english_name?.message}
                  disabled={isReadOnly}
                  inputProps={{
                    autoComplete: "off",
                    autoCorrect: "off",
                    autoCapitalize: "off",
                    spellCheck: "false",
                  }}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} data-field="chinese_name-grid">
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>中文姓名*</Typography>
              <Box
                className={`${classes.inputField} ${
                  isFieldModified("chinese_name") ? "Mui-modified" : ""
                }`}
              >
                <TextField
                  {...register("chinese_name", {
                    required: "此欄位必填",
                    maxLength: { value: 60, message: "中文姓名最多60個字符" },
                  })}
                  fullWidth
                  variant="outlined"
                  error={!!errors.chinese_name}
                  helperText={errors.chinese_name?.message}
                  disabled={isReadOnly}
                  inputProps={{
                    autoComplete: "off",
                    autoCorrect: "off",
                    autoCapitalize: "off",
                    spellCheck: "false",
                  }}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} data-field="english_name_alias-grid">
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>英文別名*</Typography>
              <Box
                className={`${classes.inputField} ${
                  isFieldModified("english_name_alias") ? "Mui-modified" : ""
                }`}
              >
                <TextField
                  {...register("english_name_alias", {
                    required: "此欄位必填",
                    maxLength: { value: 60, message: "英文別名最多60個字符" },
                  })}
                  fullWidth
                  variant="outlined"
                  error={!!errors.english_name_alias}
                  helperText={errors.english_name_alias?.message}
                  disabled={isReadOnly}
                  inputProps={{
                    autoComplete: "off",
                    autoCorrect: "off",
                    autoCapitalize: "off",
                    spellCheck: "false",
                  }}
                />
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            data-field="hkid-grid"
            className={`${classes.hkidInputWrapper} hkidInputWrapper`}
          >
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>
                香港身份證號碼*
              </Typography>
              <Box className={classes.hkidInput}>
                <Controller
                  name="hkid"
                  control={control}
                  rules={{
                    required: "此欄位必填",
                    maxLength: {
                      value: 30,
                      message: "香港身份證號碼最多30個字符",
                    },
                    pattern: {
                      value: /^[A-Za-z][A-Za-z0-9]*$/,
                      message: "第一個字符必須是英文字母",
                    },
                  }}
                  render={({ field }) => (
                    <Box
                      className={`${classes.inputField} ${
                        isFieldModified("hkid") ? "Mui-modified" : ""
                      }`}
                    >
                      <TextField
                        {...field}
                        variant="outlined"
                        style={{ width: "100%" }}
                        error={!!errors.hkid}
                        helperText={errors.hkid?.message}
                        disabled={isReadOnly}
                        inputProps={{
                          autoComplete: "off",
                          autoCorrect: "off",
                          autoCapitalize: "off",
                          spellCheck: "false",
                        }}
                      />
                    </Box>
                  )}
                />
                <Box className={classes.hkidLastDigitWrapper}>
                  <Box style={{ margin: "0 4px", color: "#CED2D6" }}>（</Box>
                  <Controller
                    name="hkid_last_digit"
                    control={control}
                    rules={{
                      required: "此欄位必填",
                      maxLength: { value: 1, message: "只能輸入一個字符" },
                      validate: {
                        singleChar: (value) =>
                          value.length <= 1 || "只能輸入一個字符",
                      },
                    }}
                    render={({ field }) => (
                      <Box>
                        <TextField
                          {...field}
                          variant="outlined"
                          style={{ width: "38px" }}
                          error={!!errors.hkid_last_digit}
                          inputProps={{
                            maxLength: 1,
                            autoComplete: "off",
                            autoCorrect: "off",
                            autoCapitalize: "off",
                            spellCheck: "false",
                          }}
                          disabled={isReadOnly}
                          className={
                            isFieldModified("hkid_last_digit")
                              ? "Mui-modified"
                              : ""
                          }
                        />
                        {errors.hkid_last_digit && (
                          <Box className={classes.hkidLastDigitError}>
                            {errors.hkid_last_digit.message}
                          </Box>
                        )}
                      </Box>
                    )}
                  />
                  <Box style={{ margin: "0 4px", color: "#CED2D6" }}>）</Box>
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            data-field="hkid-grid"
            className={`${classes.fullHKIDWrapper} fullHKIDWrapper`}
          >
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>
                香港身份證號碼*
              </Typography>
              <Box
                className={`${classes.inputField} ${
                  isFieldModified("chinese_name") ? "Mui-modified" : ""
                }`}
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  error={!!errors.chinese_name}
                  helperText={errors.chinese_name?.message}
                  disabled={isReadOnly}
                  value={`${getValues("hkid")}(${getValues(
                    "hkid_last_digit"
                  )})`}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} data-field="date_of_birth-grid">
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>出生日期*</Typography>
              <Box
                className={`${classes.inputField} ${classes.datePicker} ${
                  isFieldModified("date_of_birth") ? "Mui-modified" : ""
                }`}
              >
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <Controller
                    name="date_of_birth"
                    control={control}
                    rules={{
                      required: "此欄位必填",
                      validate: {
                        futureDate: (value) => {
                          const today = moment().startOf("day");
                          return (
                            moment(value).isBefore(today) ||
                            "出生日期不能是今天或將來的日期"
                          );
                        },
                        notCurrentYear: (value) => {
                          return (
                            moment(value).year() !== moment().year() ||
                            "出生日期不能是今年"
                          );
                        },
                      },
                    }}
                    render={({ field: { value, onChange } }) => (
                      <KeyboardDatePicker
                        fullWidth
                        inputVariant="outlined"
                        format="yyyy-MM-dd"
                        value={value || null}
                        onChange={(date) => {
                          if (date) {
                            onChange(moment(date).format("YYYY-MM-DD"));
                          } else {
                            onChange("");
                          }
                        }}
                        maxDate={moment().subtract(1, "days")}
                        error={!!errors.date_of_birth}
                        helperText={errors.date_of_birth?.message}
                        keyboardIcon={<CalendarTodayOutlinedIcon />}
                        disabled={isReadOnly}
                      />
                    )}
                  />
                </MuiPickersUtilsProvider>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} data-field="nationality-grid">
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>國籍</Typography>
              <Box
                className={`${classes.inputField} ${
                  isFieldModified("nationality") ? "Mui-modified" : ""
                }`}
              >
                <Controller
                  name="nationality"
                  control={control}
                  rules={{
                    maxLength: { value: 40, message: "國籍最多40個字符" },
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      variant="outlined"
                      fullWidth
                      error={!!errors.nationality}
                      disabled={isReadOnly}
                    >
                      {countries.map((country, index) => (
                        <MenuItem key={index} value={country.name}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.nationality && (
                  <FormHelperText error>
                    {errors.nationality.message}
                  </FormHelperText>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>性別*</Typography>
              <Box className={classes.radioGroup}>
                <Controller
                  name="gender"
                  control={control}
                  rules={{ required: "此欄位必填" }}
                  render={({ field }) => (
                    <>
                      <RadioGroup {...field}>
                        <Box className={classes.radioOption}>
                          <FormControlLabel
                            value="M"
                            control={
                              <Radio
                                className={
                                  isFieldModified("gender")
                                    ? "Mui-modified"
                                    : ""
                                }
                              />
                            }
                            label="男"
                            disabled={isReadOnly}
                          />
                        </Box>
                        <Box className={classes.radioOption}>
                          <FormControlLabel
                            value="F"
                            control={
                              <Radio
                                className={
                                  isFieldModified("gender")
                                    ? "Mui-modified"
                                    : ""
                                }
                              />
                            }
                            label="女"
                            disabled={isReadOnly}
                          />
                        </Box>
                      </RadioGroup>
                      {errors.gender && (
                        <FormHelperText error>
                          {errors.gender.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} data-field="marriage_status-grid">
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>婚姻狀況*</Typography>
              <Box className={classes.radioGroup}>
                <Controller
                  name="marriage_status"
                  control={control}
                  rules={{ required: "此欄位必填" }}
                  render={({ field }) => (
                    <>
                      <RadioGroup {...field}>
                        <Box className={classes.radioOption}>
                          <FormControlLabel
                            value="single"
                            control={
                              <Radio
                                className={
                                  isFieldModified("marriage_status")
                                    ? "Mui-modified"
                                    : ""
                                }
                              />
                            }
                            label="單身"
                            disabled={isReadOnly}
                          />
                        </Box>
                        <Box className={classes.radioOption}>
                          <FormControlLabel
                            value="married"
                            control={
                              <Radio
                                className={
                                  isFieldModified("marriage_status")
                                    ? "Mui-modified"
                                    : ""
                                }
                              />
                            }
                            label="已婚"
                            disabled={isReadOnly}
                          />
                        </Box>
                      </RadioGroup>
                      {errors.marriage_status && (
                        <FormHelperText error>
                          {errors.marriage_status.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            className={`${classes.addressField} addressInputWrapper`}
          >
            <Box className={`${classes.formField} `}>
              <Typography
                className={`${classes.greyTitle} ${classes.addressFieldLabel}`}
              >
                中文住址*
              </Typography>
              <Box
                className={`${classes.inputField} ${classes.addressField} ${
                  isFieldModified("chinese_address") ? "Mui-modified" : ""
                }`}
              >
                <TextField
                  {...register("chinese_address", {
                    required: "此欄位必填",
                    maxLength: { value: 300, message: "中文地址最多300個字符" },
                  })}
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={1}
                  error={!!errors.chinese_address}
                  helperText={errors.chinese_address?.message}
                  disabled={isReadOnly}
                  className={`${classes.inputAddressField} inputAddressField`}
                  inputProps={{
                    autoComplete: 'off',
                    autoCorrect: 'off',
                    autoCapitalize: 'off',
                    spellCheck: 'false',
                  }}
                />
              </Box>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            className={`${classes.addressField} addressFieldWrapper`}
          >
            <Box className={`${classes.formField} `}>
              <Typography
                className={`${classes.greyTitle} ${classes.addressFieldLabel}`}
              >
                中文住址*
              </Typography>
              <Box
                className={`${classes.inputField} ${classes.addressField} ${
                  isFieldModified("chinese_address") ? "Mui-modified" : ""
                }`}
              >
                <div>{getValues("chinese_address")}</div>
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            className={`${classes.addressField} addressInputWrapper`}
          >
            <Box className={`${classes.formField} `}>
              <Typography
                className={`${classes.greyTitle} ${classes.addressFieldLabel}`}
              >
                英文住址*
              </Typography>
              <Box
                className={`${classes.inputField} ${classes.addressField} ${
                  isFieldModified("english_address") ? "Mui-modified" : ""
                }`}
              >
                <TextField
                  {...register("english_address", {
                    required: "此欄位必填",
                    minLength: { value: 3, message: "英文地址最少需要3個字符" },
                    maxLength: { value: 300, message: "英文地址最多300個字符" },
                  })}
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={1}
                  error={!!errors.english_address}
                  helperText={errors.english_address?.message}
                  disabled={isReadOnly}
                  className={`${classes.inputAddressField} inputAddressField`}
                  inputProps={{
                    autoComplete: 'off',
                    autoCorrect: 'off',
                    autoCapitalize: 'off',
                    spellCheck: 'false',
                  }}
                />
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            className={`${classes.addressField} addressFieldWrapper`}
          >
            <Box className={`${classes.formField} `}>
              <Typography
                className={`${classes.greyTitle} ${classes.addressFieldLabel}`}
              >
                英文住址*
              </Typography>
              <Box
                className={`${classes.inputField} ${classes.addressField} ${
                  isFieldModified("english_address") ? "Mui-modified" : ""
                }`}
              >
                <div>{getValues("english_address")}</div>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} data-field="phone_number-grid">
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>手提電話*</Typography>
              <Box
                className={`${classes.inputField} ${
                  isFieldModified("phone_number") ? "Mui-modified" : ""
                }`}
              >
                <TextField
                  {...register("phone_number", {
                    required: "此欄位必填",
                    pattern: {
                      value: /^[0-9]+$/,
                      message: "請輸入有效的電話號碼",
                    },
                    maxLength: { value: 8, message: "電話號碼不能超過8個數字" },
                    valueAsNumber: true,
                  })}
                  fullWidth
                  variant="outlined"
                  error={!!errors.phone_number}
                  helperText={errors.phone_number?.message}
                  inputProps={{ maxLength: 8, autoComplete: 'off', autoCorrect: 'off', autoCapitalize: 'off', spellCheck: 'false'}}
                  disabled={isReadOnly}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} data-field="house_phone_number-grid">
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>住宅電話</Typography>
              <Box
                className={`${classes.inputField} ${
                  isFieldModified("house_phone_number") ? "Mui-modified" : ""
                }`}
              >
                <TextField
                  {...register("house_phone_number", {
                    pattern: {
                      value: /^[0-9]+$/,
                      message: "請輸入有效的電話號碼",
                    },
                    maxLength: { value: 8, message: "電話號碼不能超過8個數字" },
                    valueAsNumber: true,
                  })}
                  fullWidth
                  variant="outlined"
                  error={!!errors.house_phone_number}
                  helperText={errors.house_phone_number?.message}
                  inputProps={{ maxLength: 8, autoComplete: 'off', autoCorrect: 'off', autoCapitalize: 'off', spellCheck: 'false'}}
                  disabled={isReadOnly}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>電郵地址*</Typography>
              <Box
                className={`${classes.inputField} ${
                  isFieldModified("email") ? "Mui-modified" : ""
                }`}
              >
                <TextField
                  {...register("email", {
                    required: "此欄位必填",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "電子郵件格式不正確",
                    },
                  })}
                  fullWidth
                  variant="outlined"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isReadOnly}
                  inputProps={{ autoComplete: 'off', autoCorrect: 'off', autoCapitalize: 'off', spellCheck: 'false'}}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      {/* 發薪銀行資料 Section */}
      <Typography variant="h6" className={`${classes.title} formSectionTitle`}>
        發薪銀行資料
      </Typography>
      <Box className={`${classes.formSection} formSection`}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>
                戶口持有人姓名*
              </Typography>
              <Box
                className={`${classes.inputField} ${
                  isFieldModified("bank_account_holder") ? "Mui-modified" : ""
                }`}
              >
                <TextField
                  {...register("bank_account_holder", {
                    required: "此欄位必填",
                    maxLength: {
                      value: 60,
                      message: "銀行賬戶持有人最多60個字符",
                    },
                  })}
                  fullWidth
                  variant="outlined"
                  error={!!errors.bank_account_holder}
                  helperText={errors.bank_account_holder?.message}
                  disabled={isReadOnly}
                  inputProps={{ autoComplete: 'off', autoCorrect: 'off', autoCapitalize: 'off', spellCheck: 'false'}}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} data-field="bank_name-grid">
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>銀行名稱*</Typography>
              <Box
                className={`${classes.inputField} ${
                  isFieldModified("bank_name") ? "Mui-modified" : ""
                }`}
              >
                <TextField
                  {...register("bank_name", {
                    required: "此欄位必填",
                    maxLength: { value: 30, message: "銀行名稱最多30個字符" },
                  })}
                  fullWidth
                  variant="outlined"
                  error={!!errors.bank_name}
                  helperText={errors.bank_name?.message}
                  disabled={isReadOnly}
                  inputProps={{ autoComplete: 'off', autoCorrect: 'off', autoCapitalize: 'off', spellCheck: 'false'}}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} data-field="bank_account-grid">
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>戶口號碼*</Typography>
              <Box
                className={`${classes.inputField} ${
                  isFieldModified("bank_account_number") ? "Mui-modified" : ""
                }`}
              >
                <TextField
                  {...register("bank_account_number", {
                    required: "此欄位必填",
                    type: "text",
                  })}
                  fullWidth
                  variant="outlined"
                  error={!!errors.bank_account_number}
                  helperText={errors.bank_account_number?.message}
                  disabled={isReadOnly}
                  className={
                    isFieldModified("bank_account_number") ? "Mui-modified" : ""
                  }
                  inputProps={{ autoComplete: 'off', autoCorrect: 'off', autoCapitalize: 'off', spellCheck: 'false'}}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      {/* 緊急聯絡人資料 Section */}
      <Typography variant="h6" className={classes.title + " formSectionTitle"}>
        緊急聯絡人資料
      </Typography>
      <Box className={`${classes.formSection} formSection`}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} data-field="emergency_contact_name-grid">
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>
                中/英文姓名*
              </Typography>
              <Box
                className={`${classes.inputField} ${
                  isFieldModified("emergency_contact_name")
                    ? "Mui-modified"
                    : ""
                }`}
              >
                <TextField
                  {...register("emergency_contact_name", {
                    required: "此欄位必填",
                    maxLength: {
                      value: 30,
                      message: "緊急聯絡人姓名最多30個字符",
                    },
                  })}
                  fullWidth
                  variant="outlined"
                  error={!!errors.emergency_contact_name}
                  helperText={errors.emergency_contact_name?.message}
                  disabled={isReadOnly}
                  inputProps={{ autoComplete: 'off', autoCorrect: 'off', autoCapitalize: 'off', spellCheck: 'false'}}
                />
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            data-field="emergency_contact_relationship-grid"
          >
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>關係*</Typography>
              <Box
                className={`${classes.inputField} ${
                  isFieldModified("emergency_contact_relationship")
                    ? "Mui-modified"
                    : ""
                }`}
              >
                <TextField
                  {...register("emergency_contact_relationship", {
                    required: "此欄位必填",
                    maxLength: {
                      value: 30,
                      message: "緊急聯絡人關係最多30個字符",
                    },
                  })}
                  fullWidth
                  variant="outlined"
                  error={!!errors.emergency_contact_relationship}
                  helperText={errors.emergency_contact_relationship?.message}
                  disabled={isReadOnly}
                  inputProps={{ autoComplete: 'off', autoCorrect: 'off', autoCapitalize: 'off', spellCheck: 'false'}}
                />
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            data-field="emergency_contact_phone_number-grid"
          >
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>手提電話*</Typography>
              <Box
                className={`${classes.inputField} ${
                  isFieldModified("emergency_contact_phone_number")
                    ? "Mui-modified"
                    : ""
                }`}
              >
                <TextField
                  {...register("emergency_contact_phone_number", {
                    required: "此欄位必填",
                    pattern: {
                      value: /^[0-9]+$/,
                      message: "請輸入有效的電話號碼",
                    },
                    maxLength: { value: 8, message: "電話號碼不能超過8個數字" },
                    valueAsNumber: true,
                  })}
                  fullWidth
                  variant="outlined"
                  error={!!errors.emergency_contact_phone_number}
                  helperText={errors.emergency_contact_phone_number?.message}
                  inputProps={{ maxLength: 8, autoComplete: 'off', autoCorrect: 'off', autoCapitalize: 'off', spellCheck: 'false'}}
                  disabled={isReadOnly}
                />
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            data-field="emergency_contact_house_number-grid"
          >
            <Box className={classes.formField}>
              <Typography className={classes.greyTitle}>住宅電話</Typography>
              <Box
                className={`${classes.inputField} ${
                  isFieldModified("emergency_contact_house_number")
                    ? "Mui-modified"
                    : ""
                }`}
              >
                <TextField
                  {...register("emergency_contact_house_number", {
                    pattern: {
                      value: /^[0-9]+$/,
                      message: "請輸入有效的電話號碼",
                    },
                    maxLength: { value: 8, message: "電話號碼不能超過8個數字" },
                  })}
                  fullWidth
                  variant="outlined"
                  error={!!errors.emergency_contact_house_number}
                  helperText={errors.emergency_contact_house_number?.message}
                  inputProps={{ maxLength: 8, autoComplete: 'off', autoCorrect: 'off', autoCapitalize: 'off', spellCheck: 'false'}}
                  disabled={isReadOnly}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      {/* Footer */}
      <Box className={classes.footer}>
        <Typography
          variant="body2"
          className={classes.footerText + " footerText"}
        >
          請填寫上述資料及交回人力資源及行政部。如需要更新個人資料請填寫員工更新資料表格。
          <br />
          本集團會嚴謹地保密收取之僱員個人資料，並會用於僱傭關係及員工福利等用途。
        </Typography>
        <Typography
          variant="body2"
          className={classes.footerText + " footerText"}
        >
          如僱員提供的全部資料不齊全或不正確，可能會影響本集團資料的準確性及不能提供適合的員工福利給予員工。
          <br />
          但本集團可能會將上述資料轉交至其他相關部門/機構使用。
        </Typography>
        <Typography
          variant="body2"
          className={classes.footerText + " footerText"}
        >
          本人同意上述資料轉交至其他相關部門/機構使用。
          <br />
          本人亦同時確認上述提供的資料是正確，準確及完整。
        </Typography>
      </Box>
      {signature && setOpenModal && !hideButtons && (
        <Box>
          <Box>
            <Typography className={classes.blueTitle}>員工簽署</Typography>
            <Box className={classes.signatureContainer}>
              <Box className={classes.signatureBox}>
                <Box className={classes.signatureImageContainer}>
                  <img src={signature} className="signatureImage" alt="" />
                </Box>
                <hr className={classes.line} />
                <Typography className={classes.content}>
                  Signature 簽署
                </Typography>
              </Box>
              <Box className={classes.signatureBox}>
                <Typography className={classes.dateText}>
                  {signatureDate
                    ? moment(signatureDate).format("YYYY-MM-DD")
                    : moment().format("YYYY-MM-DD")}
                </Typography>
                <hr className={classes.line} />
                <Typography className={classes.content}>Date 日期</Typography>
              </Box>
            </Box>
          </Box>
          <Box className={classes.buttonContainer}>
            <Button
              startIcon={
                <CardMedia
                  component="img"
                  image={require("../icons/edit.png")}
                  alt=""
                  className={classes.media}
                />
              }
              variant="contained"
              color="primary"
              onClick={() => setOpenModal(true)}
              className={classes.editButton}
            >
              修改簽名
            </Button>

            <Button
              disabled={false}
              variant="contained"
              color="primary"
              onClick={handleComplete}
              className={classes.button}
            >
              完成
            </Button>
          </Box>
        </Box>
      )}

      {signature && setOpenModal && hideButtons && (
        <Box>
          <Box>
            <Typography className={classes.blueTitle}>員工簽署</Typography>
            <Box className={classes.signatureContainer}>
              <Box className={classes.signatureBox}>
                <Box className={classes.signatureImageContainer}>
                  <img src={signature} className="signatureImage" alt="" />
                </Box>
                <hr className={classes.line} />
                <Typography className={classes.content}>
                  Signature 簽署
                </Typography>
              </Box>
              <Box className={classes.signatureBox}>
                <Typography className={classes.dateText}>
                  {signatureDate
                    ? moment(signatureDate).format("YYYY-MM-DD")
                    : moment().format("YYYY-MM-DD")}
                </Typography>
                <hr className={classes.line} />
                <Typography className={classes.content}>Date 日期</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <ModalContainer
        open={openPasswordModal}
        setOpen={setOpenPasswordModal}
        type="isNeedPW"
        goBack={handlePasswordVerified}
      />
    </>
  );
};

export default StaffFormFields;

const useStyles = makeStyles<any, { isReadOnly: boolean }>((theme) => ({
  "@global": {
    ".pdfStyle": {
      "& .MuiOutlinedInput-input": {
        padding: "5px !important",
        fontSize: "14px !important",
      },
      "& .MuiInputAdornment-root": {
        display: "none",
      },
      "& .MuiSelect-iconOutlined": {
        display: "none",
      },
      "& .MuiTypography-root": {
        fontSize: "14px !important",
      },
    },
    ".pdfStyle.formSectionTitle": {
      marginTop: "10px !important",
    },
    ".pdfStyle.formSection": {
      marginBottom: "0px !important",
    },
    ".pdfStyle.footerText": {
      marginBottom: "10px !important",
      fontSize: "12px !important",
    },
    ".pdfStyle.MuiGrid-item": {
      padding: "0 8px !important",
    },
    ".pdfStyle.hkidInputWrapper": {
      display: "none !important",
    },
    ".pdfStyle.fullHKIDWrapper": {
      display: "block !important",
    },
    ".pdfStyle.addressFieldWrapper": {
      display: "block !important",
    },
    ".pdfStyle.addressInputWrapper": {
      display: "none !important",
    },
    ".addressFieldWrapper": {
      color: "rgb(0 0 0 / 38%)",
      fontSize: "14px !important",
      display: "none !important",
    },
    "@media print": {
      // Override all MUI grid breakpoints
      ".MuiGrid-grid-xs-12, .MuiGrid-grid-md-6": {
        flexBasis: "50% !important",
        maxWidth: "50% !important",
      },
      // Force two columns layout
      ".MuiGrid-container .MuiGrid-item": {
        flexBasis: "50% !important",
        maxWidth: "50% !important",
      },
      // Reset any responsive padding/margin
      ".MuiGrid-spacing-xs-3 > .MuiGrid-item": {
        width: "100% !important",
        padding: "0px 10px !important",
      },
      ".MuiGrid-spacing-xs-2 > .MuiGrid-item": {
        padding: "0px 8px !important",
      },
      ".MuiInputAdornment-root": {
        display: "none",
      },
      ".MuiSelect-iconOutlined": {
        display: "none",
      },
    },
  },
  title: {
    fontSize: "20px",
    letterSpacing: "4%",
    marginBottom: theme.spacing(3),
    color: "#454C96",
    fontWeight: 500,
    marginTop: "60px",
    "@media print": {
      marginTop: "10px !important",
      marginBottom: "0px !important",
    },
  },
  formSection: {
    marginBottom: "45px",
    "& .MuiGrid-container": {
      ["@media not print and  (max-width: 960px)"]: { rowGap: 0 },
      "& .MuiGrid-item": {
        "&:nth-child(odd)": { paddingLeft: "12px" },
        "&:nth-child(even)": { paddingLeft: "12px" },
        '&[data-field="english_name-grid"], &[data-field="english_name_alias-grid"], &[data-field="date_of_birth-grid"], &[data-field="phone_number-grid"], &[data-field="bank_name-grid"], &[data-field="emergency_contact_name-grid"], &[data-field="emergency_contact_phone_number-grid"]':
          {
            paddingLeft: "12px",
            ["@media (max-width: 960px)"]: { paddingLeft: "12px" },
          },
        '&[data-field="chinese_name-grid"], &[data-field="hkid-grid"], &[data-field="nationality-grid"], &[data-field="marriage_status-grid"], &[data-field="house_phone_number-grid"], &[data-field="bank_account-grid"], &[data-field="emergency_contact_relationship-grid"], &[data-field="emergency_contact_house_number-grid"]':
          {
            paddingLeft: "12px",
            ["@media (max-width: 960px)"]: { paddingLeft: "12px" },
          },
      },
    },
    "@media print": {
      marginBottom: "0px !important",
    },
  },
  formField: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    "& .MuiTypography-root": {
      marginBottom: theme.spacing(1),
      color: "#666",
    },
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      alignItems: "center",
      "& > .MuiTypography-root": {
        marginBottom: 0,
        width: "122px",
        flexShrink: 0,
      },
    },
    "@media print": {
      flexDirection: "unset",
    },
  },
  inputField: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      backgroundColor: (props) => (props.isReadOnly ? "#fff" : "inherit"),
      borderRadius: "8px",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: (props) => (props.isReadOnly ? "transparent" : "#CED2D6"),
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: (props) => (props.isReadOnly ? "transparent" : "#CED2D6"),
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: (props) => (props.isReadOnly ? "transparent" : "#CED2D6"),
      },
    },
    "&.Mui-modified": {
      "& .MuiOutlinedInput-root, & .MuiSelect-root, & .MuiInputBase-input": {
        backgroundColor: "#F4EFFF !important",
      },
    },
    "&.Mui-modified-container": {
      "& .MuiOutlinedInput-root": {
        backgroundColor: "#F4EFFF !important",
      },
      "&.MuiOutlinedInput-multiline": {
        padding: 0,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "15.5px 14px",
    },
    "& .MuiSelect-root": {
      backgroundColor: "#fff",
      borderRadius: "8px",
    },
    "& .MuiSelect-select": {
      padding: "15.5px 14px",
    },
    '&[data-field="english_name"] .MuiOutlinedInput-input, &[data-field="english_name_alias"] .MuiOutlinedInput-input, &[data-field="date_of_birth"] .MuiOutlinedInput-input, &[data-field="phone_number"] .MuiOutlinedInput-input, &[data-field="bank_name"] .MuiOutlinedInput-input, &[data-field="emergency_contact_name"] .MuiOutlinedInput-input, &[data-field="emergency_contact_phone_number"] .MuiOutlinedInput-input':
      {
        paddingRight: "20px",
      },
    "& .MuiFormHelperText-root": {
      position: "absolute",
      bottom: "-20px",
      marginTop: 0,
      marginBottom: 0,
    },
    "@media print": {
      width: "50% ",
    },
  },
  datePicker: {
    "& .MuiIconButton-root": {
      color: "#CED2D6",
    },
  },
  hkidInput: {
    display: "flex",
    alignItems: "flex-start",
    flex: 1,
    gap: theme.spacing(1),
    position: "relative",
    "& .MuiTextField-root": {
      "& .MuiOutlinedInput-root": {
        backgroundColor: "#fff",
        borderRadius: "8px",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: (props) =>
            props.isReadOnly ? "transparent" : "#CED2D6",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: (props) =>
            props.isReadOnly ? "transparent" : "#CED2D6",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: (props) =>
            props.isReadOnly ? "transparent" : "#CED2D6",
        },
      },
      "& .MuiOutlinedInput-input": {
        padding: "15px 12px",
      },
    },
    "& .MuiTypography-root": {
      color: "#666",
      fontSize: "16px",
    },
    "@media print": {
      display: "none",
    },
  },
  hkidLastDigitWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  hkidLastDigitError: {
    position: "absolute",
    top: "100%",
    left: 0,
    color: theme.palette.error.main,
    fontSize: "0.75rem",
    marginTop: "3px",
    whiteSpace: "nowrap",
  },
  footer: {
    marginTop: "20px",
    marginBottom: "20px",
  },
  footerText: {
    color: "#666",
    fontSize: "0.9rem",
    marginBottom: "30px",
  },
  greyTitle: {
    color: "#84909F !important",
    fontSize: "16px",
    fontWeight: 400,
    marginRight: theme.spacing(2),
    "@media print": {
      width: "33% !important",
      marginRight: "0px !important",
      alignContent: "center",
    },
  },
  radioGroup: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    "& .MuiFormGroup-root": {
      flexDirection: "row",
      width: "100%",
      padding: "8px",
      borderRadius: "8px",
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      marginTop: "3px",
    },
  },
  radioOption: {
    display: "flex",
    alignItems: "center",
    width: "50%",
    "& .MuiFormControlLabel-root": {
      margin: 0,
      width: "100%",
      "& .MuiTypography-root": {
        width: "auto",
        marginBottom: 0,
        marginLeft: "-15px",
        color: "#84909F",
        fontSize: "16px",
      },
    },
    "& .MuiRadio-root": {
      padding: 0,
      marginRight: 0,
      "& .MuiSvgIcon-root": {
        display: "none",
      },
      "&:hover": { backgroundColor: "transparent" },
      "&.Mui-focusVisible": { backgroundColor: "transparent" },
    },
    "& .MuiRadio-root::before": {
      content: '""',
      width: "80px",
      height: "40px",
      border: "2px solid #E0E0E0",
      borderRadius: "8px",
      display: "inline-block",
      boxSizing: "border-box",
      position: "relative",
      backgroundColor: "white",
    },
    "& .MuiRadio-root.Mui-checked::before": {
      border: "2px solid #E0E0E0",
      background: `url("${tick}") center no-repeat`,
      backgroundColor: "white",
    },
    "& .MuiRadio-root.Mui-checked.Mui-modified::before": {
      backgroundColor: "#F4EFFF",
      border: "2px solid #E0E0E0",
      background: `url("${tick}") center no-repeat`,
    },
  },
  blueTitle: {
    color: "#454C96",
    fontSize: "16px",
    fontWeight: 500,
    textAlign: "left",
    marginBottom: "20px",
  },
  signatureContainer: {
    display: "flex",
    justifyContent: "space-between",
    padding: "1rem 0",
  },
  signatureBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    maxWidth: "353px",
    justifyContent: "end",
  },
  dateText: {
    color: "#505050",
    fontSize: "14px",
    display: "flex",
    alignItems: "flex-end",
    height: "5rem",
  },
  line: {
    margin: "2rem 0",
    width: "100%",
    backgroundColor: "#505050",
    borderTop: "2px solid #505050",
    borderBottom: "0px",
    borderLeft: "0px",
    borderRight: "0px",
  },
  content: {
    color: "#505050",
    fontSize: "14px",
    fontWeight: 400,
  },
  editButton: {
    fontSize: "20px",
    fontWeight: 600,
    width: "350px",
    backgroundColor: "#908DF2",
    borderRadius: "12px",
  },
  media: {
    width: "20px",
    height: "20px",
  },
  button: {
    fontSize: "20px",
    fontWeight: 600,
    width: "350px",
    backgroundColor: "#454C96",
    borderRadius: "12px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "space-evenly",
  },
  inputAddressField: {
    "& .MuiOutlinedInput-multiline": {
      padding: "0px !important",
    },
    "& .MuiOutlinedInput-inputMultiline": {
      "@media print": {
        padding: "0px",
      },
    },
  },
  addressField: {
    "@media print": {
      minWidth: "100% !important",
      padding: "15px 8px !important",
    },
  },
  addressFieldLabel: {
    "@media print": {
      width: "130px !important",
    },
  },
  fullHKIDWrapper: {
    display: "none",
    color: "rgb(0 0 0 / 38%)",
    "@media print": {
      display: "block",
    },
  },
  hkidInputWrapper: {
    "@media print": {
      display: "none",
    },
  },
}));
